/**
 * TutorialCard Component
 * Floating card displaying tutorial content and navigation controls
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X, SkipForward, Volume2, VolumeX } from "lucide-react";
import { useTutorial } from "../../hooks/useTutorial";
import type { TutorialStep } from "./types";

interface TutorialCardProps {
  step: TutorialStep;
  currentStepNumber: number;
  totalSteps: number;
  onRequestExit: () => void;
  narration?: {
    available: boolean;
    enabled: boolean;
    isNarrating: boolean;
    onToggle: () => void;
  };
}

interface CardPosition {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  transform?: string;
}

export function TutorialCard({
  step,
  currentStepNumber,
  totalSteps,
  onRequestExit,
  narration,
}: TutorialCardProps) {
  const {
    nextStep,
    previousStep,
    skipStep,
    hasPreviousStep,
    isLastStep,
  } = useTutorial();

  const [cardPosition, setCardPosition] = useState<CardPosition>({
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  });

  // Calculate card position based on target element and preferred position
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10; // Try up to 10 times over 1 second

    const calculatePosition = () => {
      try {
        const element = document.querySelector(step.targetSelector);

        if (!element) {
          // Retry if element not found and we haven't exceeded max retries
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(calculatePosition, 100);
            return;
          }

          // Fallback to center if element still not found after retries
          console.warn(`Tutorial: Element not found after ${maxRetries} retries:`, step.targetSelector);
          setCardPosition({
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          });
          return;
        }

        const rect = element.getBoundingClientRect();

        // Check if element has valid dimensions (is actually rendered)
        if (rect.width === 0 || rect.height === 0) {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(calculatePosition, 100);
            return;
          }
        }

        const cardWidth = 400; // Approximate card width
        const cardHeight = 300; // Approximate card height
        const gap = 24; // Gap between element and card

        let position: CardPosition = {};

        switch (step.cardPosition) {
          case "top":
            position = {
              left: `${rect.left + rect.width / 2}px`,
              top: `${Math.max(gap, rect.top - cardHeight - gap)}px`,
              transform: "translateX(-50%)",
            };
            break;

          case "bottom":
            position = {
              left: `${rect.left + rect.width / 2}px`,
              top: `${rect.bottom + gap}px`,
              transform: "translateX(-50%)",
            };
            break;

          case "left":
            position = {
              right: `${window.innerWidth - rect.left + gap}px`,
              top: `${rect.top + rect.height / 2}px`,
              transform: "translateY(-50%)",
            };
            break;

          case "right":
            position = {
              left: `${rect.right + gap}px`,
              top: `${rect.top + rect.height / 2}px`,
              transform: "translateY(-50%)",
            };
            break;

          case "center":
          default:
            position = {
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            };
            break;
        }

        // Ensure card stays within viewport bounds
        const adjustedPosition = { ...position };

        // Check if card would go off screen and adjust
        if (position.left && parseFloat(position.left) + cardWidth > window.innerWidth) {
          adjustedPosition.left = `${window.innerWidth - cardWidth - gap}px`;
          adjustedPosition.transform = adjustedPosition.transform?.replace(
            "translateX(-50%)",
            ""
          );
        }

        if (position.top && parseFloat(position.top) + cardHeight > window.innerHeight) {
          adjustedPosition.top = `${window.innerHeight - cardHeight - gap}px`;
          adjustedPosition.transform = adjustedPosition.transform?.replace(
            "translateY(-50%)",
            ""
          );
        }

        setCardPosition(adjustedPosition);
      } catch (error) {
        console.error("Tutorial: Error calculating card position:", error);
        setCardPosition({
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        });
      }
    };

    // Start calculating position with initial delay
    const initialTimeout = setTimeout(calculatePosition, 300);

    window.addEventListener("resize", calculatePosition);

    return () => {
      window.removeEventListener("resize", calculatePosition);
      clearTimeout(initialTimeout);
    };
  }, [step.targetSelector, step.cardPosition]);

  const handlePrevious = () => {
    if (hasPreviousStep()) {
      previousStep();
    }
  };

  const handleNext = () => {
    nextStep();
  };

  return (
    <motion.div
      className="fixed z-[102] w-full max-w-md pointer-events-auto"
      style={cardPosition}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-surface-primary border border-border-primary rounded-lg shadow-modal p-6 mx-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-1.5 w-24 rounded-full bg-border-primary overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--d-accent)] transition-all"
                  style={{
                    width: `${(currentStepNumber / totalSteps) * 100}%`,
                  }}
                />
              </div>

              <span className="text-caption-one text-content-tertiary font-medium">
                Step {currentStepNumber} of {totalSteps}
              </span>
            </div>

            <h3 className="text-headline font-semibold text-content-primary">
              {step.title}
            </h3>
          </div>

          {narration?.available && (
            <button
              onClick={narration.onToggle}
              className={`ml-2 p-1 rounded-md hover:bg-surface-hover transition-colors ${
                narration.enabled
                  ? "text-[var(--d-accent)]"
                  : "text-content-secondary hover:text-content-primary"
              }`}
              aria-label={narration.enabled ? "Turn narration off" : "Turn narration on"}
            >
              {narration.enabled ? (
                <Volume2 className={`w-5 h-5 ${narration.isNarrating ? "animate-pulse" : ""}`} />
              ) : (
                <VolumeX className="w-5 h-5" />
              )}
            </button>
          )}

          <button
            onClick={onRequestExit}
            className="ml-2 p-1 rounded-md hover:bg-surface-hover transition-colors text-content-secondary hover:text-content-primary"
            aria-label="Exit tutorial"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-callout text-content-secondary leading-relaxed mb-6">
          {step.description}
        </p>

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={handlePrevious}
            disabled={!hasPreviousStep()}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-md text-callout font-medium transition-colors
              ${
                hasPreviousStep()
                  ? "text-content-secondary hover:text-content-primary hover:bg-surface-hover"
                  : "text-content-tertiary cursor-not-allowed opacity-50"
              }
            `}
            aria-label="Previous step"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>

          <button
            onClick={skipStep}
            className="flex items-center gap-1.5 px-3 py-2 text-callout text-content-tertiary hover:text-content-secondary transition-colors"
            aria-label="Skip step"
          >
            <SkipForward className="w-4 h-4" />
            Skip
          </button>

          <button
            onClick={handleNext}
            className="d-btn d-btn-primary"
            aria-label={isLastStep() ? "Finish tutorial" : "Next step"}
          >
            {isLastStep() ? "Finish" : "Next"}
            {!isLastStep() && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
